import {
  collection, doc, query, where, getDocs, getDoc,
  addDoc, updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

const COLLECTION = 'tires';

// ── Costanti dominio ────────────────────────────────────────────
export const TIRE_BRANDS = [
  'Pirelli', 'Michelin', 'Hankook', 'Yokohama',
  'Bridgestone', 'Continental', 'Goodyear', 'Dunlop',
  'Nankang', 'Maxxis', 'Falken', 'Kumho',
  'Avon', 'Cooper', 'Toyo', 'Federal', 'Nexen',
  'Altro',
];

export const TIRE_COMPOUNDS = [
  'Soft', 'Medium', 'Hard',
  'Super Soft', 'Super Hard',
  'Slick',
  'Inter', 'Wet',
  'Custom',
];

// Le label sono tradotte via i18n: usare `tires:status_<key>` nelle UI.
export const TIRE_STATUSES = [
  { key: 'new',       color: '#4caf50' },
  { key: 'available', color: '#2196f3' },
  { key: 'mounted',   color: '#ff9800' },
  { key: 'used',      color: '#9e9e9e' },
  { key: 'scrapped',  color: '#f44336' },
  { key: 'sealed',    color: '#673ab7' },
];

export const TIRE_POSITIONS = ['FL', 'FR', 'RL', 'RR'];

// ── CRUD ────────────────────────────────────────────────────────

/**
 * Restituisce tutte le gomme di un team.
 */
export async function getTires(teamId) {
  if (!teamId) return [];
  const q = query(collection(db, COLLECTION), where('teamId', '==', teamId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/**
 * Restituisce una singola gomma per ID.
 */
export async function getTire(tireId) {
  if (!tireId) return null;
  const ref = doc(db, COLLECTION, tireId);
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Cerca una gomma per barcode all'interno di un team.
 * Ritorna null se non esiste.
 */
export async function findTireByBarcode(teamId, barcode) {
  if (!teamId || !barcode) return null;
  const q = query(
    collection(db, COLLECTION),
    where('teamId', '==', teamId),
    where('barcode', '==', barcode.trim()),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const d = snap.docs[0];
  return { id: d.id, ...d.data() };
}

/**
 * Crea una nuova gomma. Blocca se barcode già esistente nel team.
 */
export async function createTire(teamId, userId, tireData) {
  if (!teamId) throw new Error('teamId mancante');
  if (!tireData.barcode?.trim()) throw new Error('Barcode obbligatorio');

  // Anti-duplicato
  const existing = await findTireByBarcode(teamId, tireData.barcode);
  if (existing) {
    const err = new Error(`Gomma già registrata: ${existing.brand || ''} ${existing.compound || ''}`.trim());
    err.code = 'duplicate';
    err.existing = existing;
    throw err;
  }

  const payload = {
    teamId,
    barcode:      tireData.barcode.trim(),
    brand:        tireData.brand        || '',
    compound:     tireData.compound     || '',
    dotCode:      tireData.dotCode      || '',
    dimensions:   tireData.dimensions   || '',
    vehicleId:    tireData.vehicleId    || null,
    position:     tireData.position     || null,
    status:       tireData.status       || 'new',
    kmTotal:      Number(tireData.kmTotal)      || 0,
    heatCycles:   Number(tireData.heatCycles)   || 0,
    lifeMaxKm:    tireData.lifeMaxKm    != null ? Number(tireData.lifeMaxKm)    : null,
    lifeMaxCycles:tireData.lifeMaxCycles!= null ? Number(tireData.lifeMaxCycles): null,
    sealed:       !!tireData.sealed,
    notes:        tireData.notes        || '',
    createdAt:    serverTimestamp(),
    updatedAt:    serverTimestamp(),
    createdBy:    userId || null,
  };

  const ref = await addDoc(collection(db, COLLECTION), payload);
  return { id: ref.id, ...payload };
}

/**
 * Aggiorna una gomma esistente.
 */
export async function updateTire(tireId, patch) {
  if (!tireId) throw new Error('tireId mancante');
  const cleanPatch = { ...patch, updatedAt: serverTimestamp() };
  // Normalizzazioni numeriche
  ['kmTotal', 'heatCycles', 'lifeMaxKm', 'lifeMaxCycles'].forEach(k => {
    if (k in cleanPatch && cleanPatch[k] !== null && cleanPatch[k] !== '') {
      cleanPatch[k] = Number(cleanPatch[k]);
    }
  });
  await updateDoc(doc(db, COLLECTION, tireId), cleanPatch);
}

/**
 * Cancella una gomma.
 */
export async function deleteTire(tireId) {
  if (!tireId) throw new Error('tireId mancante');
  await deleteDoc(doc(db, COLLECTION, tireId));
}

/**
 * Helper: calcola se una gomma è vicina al fine vita (>= 80% delle soglie).
 */
export function isNearEndOfLife(tire) {
  if (!tire) return false;
  const kmRatio    = tire.lifeMaxKm     ? tire.kmTotal    / tire.lifeMaxKm     : 0;
  const cycleRatio = tire.lifeMaxCycles ? tire.heatCycles / tire.lifeMaxCycles : 0;
  return kmRatio >= 0.8 || cycleRatio >= 0.8;
}

/**
 * Helper: testo di lifing compatto per badge.
 */
export function formatLifing(tire) {
  if (!tire) return '';
  const parts = [];
  if (tire.kmTotal != null)    parts.push(`${tire.kmTotal} km`);
  if (tire.heatCycles != null) parts.push(`${tire.heatCycles} cicli`);
  return parts.join(' · ');
}
